import { useState } from "react";
import { useTranslation } from "react-i18next";

import Modal, { ModalButton, ModalSecondaryButton } from "../Modal";
import { Field, Scale, ScaleValue } from "./styles";

const prefix = "components.nps";

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** The follow-up asked after the score: what was missing (0–6), what would make it a 10 (7–8), what they like (9–10) */
const followUp = (score: number) => (score <= 6 ? "detractor" : score <= 8 ? "passive" : "promoter");

type Props = {
  /** A score was picked (the answer counts from here, even if the comment is skipped) */
  onScore: (score: number) => void;
  /** The comment was sent (only when written) */
  onComment: (comment: string) => void;
  /** Closed before scoring */
  onDismiss: () => void;
  /** The modal goes away (after onDismiss, skipping the comment or after the thanks) */
  onClose: () => void;
};

/**
 * The NPS survey: "from 0 to 10, how likely are you to recommend Keepo", then an optional comment with
 * a question that fits the score, then thanks. Only shows it: saving is up to the caller.
 * Closed only by the X: a click outside (or Escape) would end it by accident.
 */
const NpsModal = ({ onScore, onComment, onDismiss, onClose }: Props) => {
  const { t } = useTranslation();
  const [score, setScore] = useState<number>();
  const [comment, setComment] = useState("");
  const [isDone, setDone] = useState(false);
  const exit = () => {
    if (score === undefined) {
      onDismiss();
    }
    onClose();
  };

  const pick = (value: number) => {
    setScore(value);
    onScore(value);
  };

  const send = () => {
    const text = comment.trim();
    if (!text) {
      onClose();
      return;
    }
    onComment(text);
    setDone(true);
  };

  if (isDone) {
    return (
      <Modal
        onExit={exit}
        exitLabel={t(`${prefix}.exit`)}
        title={t(`${prefix}.thanks.title`)}
        subtitle={t(`${prefix}.thanks.description`)}
        footer={<ModalButton onClick={onClose}>{t(`${prefix}.thanks.close`)}</ModalButton>}
      />
    );
  }

  if (score === undefined) {
    return (
      <Modal onExit={exit} exitLabel={t(`${prefix}.exit`)} title={t(`${prefix}.question`)}>
        <Scale>
          <div className="values" role="group" aria-label={t(`${prefix}.question`)}>
            {SCORES.map(value => (
              <ScaleValue
                key={value}
                type="button"
                aria-label={t(`${prefix}.score`, { score: value })}
                onClick={() => pick(value)}>
                {value}
              </ScaleValue>
            ))}
          </div>
          <div className="labels" aria-hidden="true">
            <span>{t(`${prefix}.low`)}</span>
            <span>{t(`${prefix}.high`)}</span>
          </div>
        </Scale>
      </Modal>
    );
  }

  return (
    <Modal
      onExit={exit}
      exitLabel={t(`${prefix}.exit`)}
      title={t(`${prefix}.followUp.${followUp(score)}`)}
      footer={
        <>
          <ModalSecondaryButton type="button" onClick={onClose}>
            {t(`${prefix}.skip`)}
          </ModalSecondaryButton>
          <ModalButton onClick={send}>{t(`${prefix}.send`)}</ModalButton>
        </>
      }>
      <Field>
        <textarea
          value={comment}
          maxLength={1000}
          aria-label={t(`${prefix}.followUp.${followUp(score)}`)}
          placeholder={t(`${prefix}.placeholder`)}
          autoFocus
          onChange={event => setComment(event.target.value)}
        />
      </Field>
    </Modal>
  );
};

export default NpsModal;
